// src/api/insights.js
import api from '../utils/api';

/**
 * Insights API service
 * Handles all insights-related API calls with caching support
 */
class InsightsAPI {
  constructor() {
    this.baseUrl = '/api/insights';
  }

  /**
   * Prime insights data (background prefetch)
   * Fire-and-forget call triggered on login
   * @returns {Promise} API response
   */
  async primeInsights() {
    try {
      console.log('🚀 Triggering insights prefetch...');
      const response = await api.post(`${this.baseUrl}/prime`);
      console.log('✅ Insights prefetch initiated:', response.data);
      return response.data;
    } catch (error) {
      // Don't throw error for prefetch failures - it's background operation
      console.warn('⚠️ Insights prefetch failed (non-blocking):', error.response?.data?.message || error.message);
      return { 
        status: 'failed', 
        message: error.response?.data?.message || error.message,
        nonBlocking: true 
      };
    }
  }

  /**
   * Get cached insights data
   * @param {String} type - Type of insights ('events' or 'news')
   * @param {Object} preferences - User preferences
   * @returns {Promise} API response with insights data
   */
  async getCachedInsights(type, preferences) {
    try {
      const response = await api.post(`${this.baseUrl}/cached`, {
        type,
        preferences
      });
      return response.data;
    } catch (error) {
      console.error(`Error getting cached ${type}:`, error);
      throw error;
    }
  }

  /**
   * Get events summary (backward compatible)
   * Now uses caching under the hood
   * @param {Object} preferences - User preferences
   * @returns {Promise} Events data
   */
  async getEventsSummary(preferences) {
    try {
      const response = await api.post(`${this.baseUrl}/events-summary`, {
        preferences
      });
      return response.data;
    } catch (error) {
      // 402 is a plan boundary, not a fault; callers render an upgrade state.
      if (error.response?.status !== 402) {
        console.error('Error getting events summary:', error);
      }
      throw error;
    }
  }

  /**
   * Get news summary (backward compatible)
   * Now uses caching under the hood
   * @param {Object} preferences - User preferences
   * @param {String} timezone - User timezone
   * @returns {Promise} News data
   */
  async getNewsSummary(preferences, timezone = 'UTC') {
    try {
      const response = await api.post(`${this.baseUrl}/news-summary`, {
        preferences,
        timezone
      });
      return response.data;
    } catch (error) {
      if (error.response?.status !== 402) {
        console.error('Error getting news summary:', error);
      }
      throw error;
    }
  }

  /**
   * Get analytics summary
   * @returns {Promise} Analytics data
   */
  async getAnalyticsSummary() {
    try {
      const response = await api.get(`${this.baseUrl}/analytics-summary`);
      return response.data;
    } catch (error) {
      console.error('Error getting analytics summary:', error);
      throw error;
    }
  }

  /**
   * Get cache statistics (for debugging/admin)
   * @returns {Promise} Cache stats
   */
  async getCacheStats() {
    try {
      const response = await api.get(`${this.baseUrl}/cache/stats`);
      return response.data;
    } catch (error) {
      console.error('Error getting cache stats:', error);
      throw error;
    }
  }

  /**
   * Clear user's cache
   * @param {String} type - Optional type to clear ('events' or 'news'), or null for all
   * @returns {Promise} Clear result
   */
  async clearMyCache(type = null) {
    try {
      const response = await api.delete(`${this.baseUrl}/cache/my`, {
        data: type ? { type } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error clearing cache:', error);
      throw error;
    }
  }

  /**
   * Track content click for analytics
   * @param {Object} clickData - Click tracking data
   * @returns {Promise} Tracking result
   */
  async trackContentClick(clickData) {
    try {
      const response = await api.post(`${this.baseUrl}/track/click`, clickData);
      return response.data;
    } catch (error) {
      console.error('Error tracking content click:', error);
      // Don't throw for tracking errors
      return { success: false, error: error.message };
    }
  }

  /**
   * Check if insights prefetch is enabled
   * @returns {Boolean} True if prefetch is enabled
   */
  isPrefetchEnabled() {
    // Check environment variable or config
    // In production builds, this would come from build-time env vars
    return process.env.REACT_APP_INSIGHTS_PREFETCH_ENABLED !== 'false';
  }

  /**
   * Get prefetch configuration
   * @returns {Object} Prefetch configuration
   */
  getPrefetchConfig() {
    return {
      enabled: this.isPrefetchEnabled(),
      timeout: parseInt(process.env.REACT_APP_INSIGHTS_PREFETCH_TIMEOUT) || 30000,
      retryDelay: parseInt(process.env.REACT_APP_INSIGHTS_PREFETCH_RETRY_DELAY) || 5000
    };
  }
}

// Create and export singleton instance
const insightsAPI = new InsightsAPI();

export default insightsAPI;

// Named exports for specific functions
export const {
  primeInsights,
  getCachedInsights,
  getEventsSummary,
  getNewsSummary,
  getAnalyticsSummary,
  getCacheStats,
  clearMyCache,
  trackContentClick,
  isPrefetchEnabled,
  getPrefetchConfig
} = insightsAPI;
